// The economics panel's two charts (PE9): monthly margin on recognized revenue against the target,
// and cumulative cash. Single series each, so no legend: the title names what is plotted. Thin marks,
// recessive hairline axes, a hover readout on every month, and a table view for every value.

import { useCodeLabels } from "../codes";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { MonthEconomics } from "../../../core";
import { money, pct } from "../format";

const INK = { primary: "#2B3C4D", secondary: "#46576F", muted: "#8C97A8", grid: "#E7E6E6" };
const SERIES = "#46576F"; // DUSK
const PAD = { top: 14, right: 64, bottom: 24, left: 52 };
const HEIGHT = 190;

/** Months read YYYY-MM (Chris, 2026-10-14). */
const label = (m: string, _long = false) => m.slice(0, 7);

function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.floor(e.contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** Clean tick values spanning [lo, hi]: steps of 1, 2 or 5 × a power of ten. */
function ticks(lo: number, hi: number, count = 4): number[] {
  const span = hi - lo || 1;
  const raw = span / count;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= raw)!;
  const out: number[] = [];
  for (let t = Math.ceil(lo / step) * step; t <= hi + 1e-9; t += step) out.push(Math.round(t / step) * step);
  return out;
}

const compactMoney = (n: number) => {
  const a = Math.abs(n);
  const s = a >= 1_000_000 ? `$${(a / 1_000_000).toFixed(1)}M` : a >= 1_000 ? `$${Math.round(a / 1_000)}K` : `$${Math.round(a)}`;
  return n < 0 ? `−${s}` : s;
};

/** Which month labels fit under the axis: every nth, always the first. */
const labelEvery = (count: number, plotWidth: number) => Math.max(1, Math.ceil(count / Math.max(1, Math.floor(plotWidth / 52))));

function Tooltip({ x, y, width, children }: { x: number; y: number; width: number; children: ReactNode }) {
  const left = Math.min(Math.max(8, x - 90), width - 188);
  return (
    <div className="chart-tip" style={{ left, top: y }} role="status">
      {children}
    </div>
  );
}

/** A column per month: contribution margin % of recognized revenue, with the target as a reference line. */
export function MarginChart({ months, target }: { months: MonthEconomics[]; target: number }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const values = months.map((m) => m.marginPct).filter((v): v is number => v !== null);
  const lo = Math.max(-1, Math.min(0, target, ...values) - 0.05);
  const hi = Math.min(1, Math.max(target, ...values, 0.1) + 0.05);
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const slot = months.length ? plotW / months.length : 0;
  const barW = Math.min(24, slot * 0.6);
  const y = (v: number) => PAD.top + ((hi - v) / (hi - lo)) * plotH;
  const every = labelEvery(months.length, plotW);
  const r = Math.min(4, barW / 2);

  // A column with a 4px rounded data-end and a square end at the zero baseline.
  const column = (cx: number, v: number) => {
    const clamped = Math.max(lo, Math.min(hi, v));
    const y0 = y(0);
    const y1 = y(clamped);
    const x0 = cx - barW / 2;
    const x1 = cx + barW / 2;
    const h = Math.abs(y1 - y0);
    const rr = Math.min(r, h);
    if (h < 0.5) return "";
    return y1 < y0
      ? `M${x0},${y0} V${y1 + rr} Q${x0},${y1} ${x0 + rr},${y1} H${x1 - rr} Q${x1},${y1} ${x1},${y1 + rr} V${y0} Z`
      : `M${x0},${y0} V${y1 - rr} Q${x0},${y1} ${x0 + rr},${y1} H${x1 - rr} Q${x1},${y1} ${x1},${y1 - rr} V${y0} Z`;
  };

  const h = hover !== null ? months[hover] : null;
  return (
    <div className="chart" ref={ref}>
      {width > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label={`Monthly contribution margin against a ${pct(target, 0)} target`}>
          {ticks(lo, hi).map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)} stroke={INK.grid} strokeWidth={1} />
              <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" className="axis-text">
                {pct(t, 0)}
              </text>
            </g>
          ))}
          <line x1={PAD.left} x2={PAD.left + plotW} y1={y(0)} y2={y(0)} stroke={INK.muted} strokeWidth={1} />
          <line x1={PAD.left} x2={PAD.left + plotW} y1={y(target)} y2={y(target)} stroke={INK.secondary} strokeWidth={1} />
          <text x={PAD.left + plotW + 6} y={y(target) + 4} className="axis-text strong">
            {pct(target, 0)} target
          </text>
          {months.map((m, i) => {
            const cx = PAD.left + slot * i + slot / 2;
            return (
              <g key={m.month}>
                {m.marginPct !== null && <path d={column(cx, m.marginPct)} fill={SERIES} opacity={hover === null || hover === i ? 1 : 0.55} />}
                {i % every === 0 && (
                  <text x={cx} y={HEIGHT - 6} textAnchor="middle" className="axis-text">
                    {label(m.month)}
                  </text>
                )}
                <rect
                  x={PAD.left + slot * i}
                  y={PAD.top}
                  width={slot}
                  height={plotH}
                  fill="transparent"
                  tabIndex={0}
                  aria-label={`${label(m.month, true)}: ${m.marginPct === null ? "no revenue recognized" : `${pct(m.marginPct)} margin`}`}
                  onPointerEnter={() => setHover(i)}
                  onPointerLeave={() => setHover(null)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                />
              </g>
            );
          })}
        </svg>
      )}
      {h && (
        <Tooltip x={PAD.left + slot * hover! + slot / 2} y={PAD.top} width={width}>
          <strong>{h.marginPct === null ? "No revenue" : pct(h.marginPct)}</strong> margin, {label(h.month, true)}
          <div className="tip-rows">
            <span>Revenue {money(h.revenue)}</span>
            <span>Labor {money(h.labor)}</span>
            {h.expenses > 0 && <span>Other costs {money(h.expenses)}</span>}
          </div>
        </Tooltip>
      )}
    </div>
  );
}

/** Cumulative cash: invoices in, labor and costs out, from the first month on. */
export function CashChart({ months }: { months: MonthEconomics[] }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const values = months.map((m) => m.cumulativeCash);
  const lo = Math.min(0, ...values);
  const hi = Math.max(0, ...values, 1);
  const pad = (hi - lo) * 0.08;
  const [ylo, yhi] = [lo - (lo < 0 ? pad : 0), hi + pad];
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (months.length > 1 ? (plotW * i) / (months.length - 1) : plotW / 2);
  const y = (v: number) => PAD.top + ((yhi - v) / (yhi - ylo)) * plotH;
  const every = labelEvery(months.length, plotW);
  const line = months.map((m, i) => `${i ? "L" : "M"}${x(i)},${y(m.cumulativeCash)}`).join(" ");
  const area = months.length ? `${line} L${x(months.length - 1)},${y(0)} L${x(0)},${y(0)} Z` : "";
  const last = months.at(-1);

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const box = (e.currentTarget as SVGRectElement).getBoundingClientRect();
    const i = Math.round(((e.clientX - box.left) / box.width) * (months.length - 1));
    setHover(Math.max(0, Math.min(months.length - 1, i)));
  };
  const h = hover !== null ? months[hover] : null;
  return (
    <div className="chart" ref={ref}>
      {width > 0 && months.length > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label="Cumulative cash by month">
          {ticks(ylo, yhi).map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)} stroke={INK.grid} strokeWidth={1} />
              <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" className="axis-text">
                {compactMoney(t)}
              </text>
            </g>
          ))}
          <line x1={PAD.left} x2={PAD.left + plotW} y1={y(0)} y2={y(0)} stroke={INK.muted} strokeWidth={1} />
          <path d={area} fill={SERIES} opacity={0.1} />
          <path d={line} fill="none" stroke={SERIES} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          {months.map((m, i) =>
            i % every === 0 ? (
              <text key={m.month} x={x(i)} y={HEIGHT - 6} textAnchor="middle" className="axis-text">
                {label(m.month)}
              </text>
            ) : null,
          )}
          {last && (
            <>
              <circle cx={x(months.length - 1)} cy={y(last.cumulativeCash)} r={4} fill={SERIES} stroke="#FFFFFF" strokeWidth={2} />
              <text x={x(months.length - 1) + 8} y={y(last.cumulativeCash) + 4} className="axis-text strong">
                {compactMoney(last.cumulativeCash)}
              </text>
            </>
          )}
          {h && <line x1={x(hover!)} x2={x(hover!)} y1={PAD.top} y2={PAD.top + plotH} stroke={INK.secondary} strokeWidth={1} />}
          <rect x={PAD.left} y={PAD.top} width={plotW} height={plotH} fill="transparent" onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
        </svg>
      )}
      {h && (
        <Tooltip x={x(hover!)} y={PAD.top} width={width}>
          <strong>{money(h.cumulativeCash)}</strong> cumulative, {label(h.month, true)}
          <div className="tip-rows">
            <span>In {money(h.cashIn)}</span>
            <span>Out {money(h.cashOut)}</span>
          </div>
        </Tooltip>
      )}
    </div>
  );
}

// --- Projects dashboard (PE15) ------------------------------------------------------------------------

/** Booked is the darker step and pipeline the lighter one: the same revenue at two levels of certainty. */
const BOOKED = "#46576F"; // DUSK
const PIPELINE = "#88B1C9"; // SKY
const GAP = 2; // surface gap between stacked segments

export interface RevenueMonth {
  month: string;
  booked: number;
  /** Weighted by probability. */
  pipeline: number;
  /** At full value. */
  pipelineFull?: number;
  cost: number;
}

/** Pipeline shown either weighted by probability or at full value (rev. 1.16). */
const pipelineOf = (m: RevenueMonth, full: boolean) => (full ? (m.pipelineFull ?? m.pipeline) : m.pipeline);
/** The least height a non-zero pipeline segment is drawn at, so a small one is still seen. */
const MIN_SEGMENT = 3;

/** A column path from yBottom up to yTop, square at the bottom, with a 4px rounded top when it ends the stack. */
function columnPath(x0: number, x1: number, yTop: number, yBottom: number, roundTop: boolean) {
  const h = yBottom - yTop;
  if (h < 0.5) return "";
  const r = roundTop ? Math.min(4, h, (x1 - x0) / 2) : 0;
  return `M${x0},${yBottom} V${yTop + r} Q${x0},${yTop} ${x0 + r},${yTop} H${x1 - r} Q${x1},${yTop} ${x1},${yTop + r} V${yBottom} Z`;
}

export function RevenueLegend({ full = false }: { full?: boolean }) {
  return (
    <div className="legend" aria-hidden="true">
      <span>
        <i style={{ background: BOOKED }} /> Booked
      </span>
      <span>
        <i style={{ background: PIPELINE }} /> Pipeline, {full ? "at full value" : "weighted by probability"}
      </span>
    </div>
  );
}

/**
 * Revenue by month: booked, with pipeline stacked on top. Click a month to select it (the dashboard
 * filters Margin by project to it); click it again to clear.
 */
export function RevenueChart({
  months,
  full = false,
  selected = null,
  onSelect,
}: {
  months: RevenueMonth[];
  full?: boolean;
  selected?: string | null;
  onSelect?: (month: string | null) => void;
}) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const hi = Math.max(1, ...months.map((m) => m.booked + pipelineOf(m, full))) * 1.08;
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const slot = months.length ? plotW / months.length : 0;
  const barW = Math.min(24, slot * 0.6);
  const y = (v: number) => PAD.top + ((hi - v) / hi) * plotH;
  const every = labelEvery(months.length, plotW);
  const h = hover !== null ? months[hover] : null;
  return (
    <div className="chart" ref={ref}>
      {width > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label="Revenue by month: booked, plus pipeline weighted by probability">
          {ticks(0, hi).map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)} stroke={INK.grid} strokeWidth={1} />
              <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" className="axis-text">
                {compactMoney(t)}
              </text>
            </g>
          ))}
          <line x1={PAD.left} x2={PAD.left + plotW} y1={y(0)} y2={y(0)} stroke={INK.muted} strokeWidth={1} />
          {months.map((m, i) => {
            const cx = PAD.left + slot * i + slot / 2;
            const [x0, x1] = [cx - barW / 2, cx + barW / 2];
            const pipe = pipelineOf(m, full);
            const yBooked = y(m.booked);
            // Pipeline sits on booked across a 2px gap, and is never drawn thinner than MIN_SEGMENT.
            const pipelineBottom = m.booked > 0 ? yBooked - GAP : y(0);
            const pipelineTop = Math.min(y(m.booked + pipe), pipelineBottom - MIN_SEGMENT);
            const showPipeline = pipe > 0;
            const focus = hover ?? (selected ? months.findIndex((x) => x.month === selected) : -1);
            const dim = focus < 0 || focus === i ? 1 : 0.55;
            return (
              <g key={m.month}>
                <path d={columnPath(x0, x1, yBooked, y(0), !showPipeline)} fill={BOOKED} opacity={dim} />
                {showPipeline && <path d={columnPath(x0, x1, pipelineTop, pipelineBottom, true)} fill={PIPELINE} opacity={dim} />}
                {i % every === 0 && (
                  <text x={cx} y={HEIGHT - 6} textAnchor="middle" className="axis-text">
                    {label(m.month)}
                  </text>
                )}
                <rect
                  x={PAD.left + slot * i}
                  y={PAD.top}
                  width={slot}
                  height={plotH}
                  fill="transparent"
                  tabIndex={0}
                  aria-label={`${label(m.month, true)}: booked ${money(m.booked)}, pipeline ${money(pipe)}${onSelect ? (selected === m.month ? ". Selected; press to clear" : ". Press to filter by this month") : ""}`}
                  aria-pressed={onSelect ? selected === m.month : undefined}
                  role={onSelect ? "button" : undefined}
                  style={onSelect ? { cursor: "pointer" } : undefined}
                  onClick={() => onSelect?.(selected === m.month ? null : m.month)}
                  onKeyDown={(e) => {
                    if (onSelect && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      onSelect(selected === m.month ? null : m.month);
                    }
                  }}
                  onPointerEnter={() => setHover(i)}
                  onPointerLeave={() => setHover(null)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                />
              </g>
            );
          })}
        </svg>
      )}
      {h && (
        <Tooltip x={PAD.left + slot * hover! + slot / 2} y={PAD.top} width={width}>
          <strong>{money(h.booked + pipelineOf(h, full))}</strong> in {label(h.month, true)}
          <div className="tip-rows">
            <span>Booked {money(h.booked)}</span>
            <span>Pipeline {money(h.pipeline)} weighted, {money(h.pipelineFull ?? h.pipeline)} at full value</span>
            <span>Booked cost {money(h.cost)}</span>
            {h.booked > 0 && <span>Booked margin {pct((h.booked - h.cost) / h.booked)}</span>}
          </div>
        </Tooltip>
      )}
    </div>
  );
}

export function RevenueTable({ months }: { months: RevenueMonth[] }) {
  return (
    <div className="table-scroll">
      <table className="month-table">
        <thead>
          <tr>
            <th>Month</th>
            <th className="num">Booked</th>
            <th className="num">Pipeline (weighted)</th>
            <th className="num">Pipeline (full value)</th>
            <th className="num">Total</th>
            <th className="num">Booked cost</th>
            <th className="num">Booked margin</th>
          </tr>
        </thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.month}>
              <td>{label(m.month, true)}</td>
              <td className="num">{money(m.booked)}</td>
              <td className="num">{money(m.pipeline)}</td>
              <td className="num">{money(m.pipelineFull ?? m.pipeline)}</td>
              <td className="num">{money(m.booked + m.pipeline)}</td>
              <td className="num">{money(m.cost)}</td>
              <td className="num">{m.booked ? pct((m.booked - m.cost) / m.booked) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface ProjectMargin {
  id: string;
  code: string;
  name: string;
  revenue: number;
  cost: number;
  contributionPct: number | null;
  targetMargin: number;
}

/**
 * Contribution margin per project, worst first, with each project's target as a tick. A bar below its
 * target is DAWN and says so in words; one that meets it is DUSK.
 */
export function MarginBars({ projects, link }: { projects: ProjectMargin[]; link: (id: string) => string }) {
  const { label: codeLabel } = useCodeLabels();
  const rows = projects.filter((p) => p.contributionPct !== null);
  const values = rows.flatMap((p) => [p.contributionPct!, p.targetMargin]);
  // Headroom past the largest value, so a target tick never sits on the track's edge.
  const lo = Math.max(-1, Math.min(0, ...values) - (Math.min(0, ...values) < 0 ? 0.05 : 0));
  const hi = Math.min(1, Math.max(0.1, ...values) + 0.05);
  const at =(v: number) => `${((Math.max(lo, Math.min(hi, v)) - lo) / (hi - lo)) * 100}%`;
  return (
    <div className="mbars">
      {rows.map((p) => {
        const v = p.contributionPct!;
        const below = v < p.targetMargin - 1e-9;
        const [from, to] = v >= 0 ? [0, v] : [v, 0];
        return (
          <a key={p.id} href={link(p.id)} className="mbar-row" aria-label={`${p.code} ${p.name}: ${pct(v)} margin, ${below ? "below" : "meets"} its ${pct(p.targetMargin, 0)} target`}>
            <span className="mbar-label" title={`${codeLabel(p.code)} — ${p.name}`}>
              <strong>{codeLabel(p.code)}</strong> {p.name}
            </span>
            <span className="mbar-track">
              <span className="mbar-zero" style={{ left: at(0) }} />
              <span
                className={`mbar ${v >= 0 ? "pos" : "neg"}`}
                style={{ left: at(from), width: `calc(${at(to)} - ${at(from)})`, background: below ? "#941100" : BOOKED }}
              />
              <span className="mbar-target" style={{ left: at(p.targetMargin) }} title={`${pct(p.targetMargin, 0)} target`} />
            </span>
            <span className="mbar-value">
              {pct(v)} <span className="muted">{below ? "↓ below" : "✓"}</span>
            </span>
            <span className="mbar-sub">
              {money(p.revenue)} revenue · {money(p.cost)} cost
            </span>
          </a>
        );
      })}
    </div>
  );
}

/** Every value the charts draw, as a table (accessibility, and for checking the numbers). */
export function MonthTable({ months }: { months: MonthEconomics[] }) {
  return (
    <div className="table-scroll">
      <table className="month-table">
        <thead>
          <tr>
            <th>Month</th>
            <th className="num">Hours</th>
            <th className="num">Revenue</th>
            <th className="num">Labor</th>
            <th className="num">Other costs</th>
            <th className="num">Margin</th>
            <th className="num">Margin %</th>
            <th className="num">Cash in</th>
            <th className="num">Cash out</th>
            <th className="num">Cumulative</th>
          </tr>
        </thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.month}>
              <td>{label(m.month, true)}</td>
              <td className="num">{Math.round(m.hours).toLocaleString("en-US")}</td>
              <td className="num">{money(m.revenue)}</td>
              <td className="num">{money(m.labor)}</td>
              <td className="num">{money(m.expenses)}</td>
              <td className="num">{money(m.margin)}</td>
              <td className="num">{m.marginPct === null ? "—" : pct(m.marginPct)}</td>
              <td className="num">{money(m.cashIn)}</td>
              <td className="num">{money(m.cashOut)}</td>
              <td className="num">{money(m.cumulativeCash)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- Profitability (PF1, rev. 1.20) ------------------------------------------------------------------

const PROFIT = "#46576F"; // DUSK
const LOSS = "#941100"; // DAWN: below the zero line as well as red, so sign never rests on color alone

export interface ProfitChartMonth {
  month: string;
  revenue: number;
  costs: number;
  profit: number;
  margin: number | null;
  cumulative: number;
}

export function ProfitLegend() {
  return (
    <div className="legend" aria-hidden="true">
      <span>
        <i style={{ background: PROFIT }} /> Profit
      </span>
      <span>
        <i style={{ background: LOSS }} /> Loss
      </span>
    </div>
  );
}

/**
 * A column per month: profit above the zero line, loss below it. Hover or focus a month for the
 * build-up; click it to select it (Profitability breaks the selected month down), and again to clear.
 */
export function ProfitChart({ months, selected = null, onSelect }: { months: ProfitChartMonth[]; selected?: string | null; onSelect?: (month: string | null) => void }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const hi = Math.max(1, ...months.map((m) => m.profit)) * 1.08;
  const lo = Math.min(0, ...months.map((m) => m.profit)) * 1.08;
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const slot = months.length ? plotW / months.length : 0;
  const barW = Math.min(24, slot * 0.6);
  const y = (v: number) => PAD.top + ((hi - v) / (hi - lo)) * plotH;
  const every = labelEvery(months.length, plotW);
  const h = hover !== null ? months[hover] : null;
  return (
    <div className="chart" ref={ref}>
      {width > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label="Profit by month">
          {ticks(lo, hi).map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)} stroke={INK.grid} strokeWidth={1} />
              <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" className="axis-text">
                {compactMoney(t)}
              </text>
            </g>
          ))}
          <line x1={PAD.left} x2={PAD.left + plotW} y1={y(0)} y2={y(0)} stroke={INK.muted} strokeWidth={1} />
          {months.map((m, i) => {
            const cx = PAD.left + slot * i + slot / 2;
            const [x0, x1] = [cx - barW / 2, cx + barW / 2];
            const focus = hover ?? (selected ? months.findIndex((x) => x.month === selected) : -1);
            const dim = focus < 0 || focus === i ? 1 : 0.55;
            const up = m.profit >= 0;
            // Loss columns hang from the zero line: drawn as a column flipped about it.
            const d = up ? columnPath(x0, x1, y(m.profit), y(0), true) : columnPath(x0, x1, 2 * y(0) - y(m.profit), y(0), true);
            return (
              <g key={m.month}>
                <path d={d} fill={up ? PROFIT : LOSS} opacity={dim} transform={up ? undefined : `matrix(1 0 0 -1 0 ${2 * y(0)})`} />
                {i % every === 0 && (
                  <text x={cx} y={HEIGHT - 6} textAnchor="middle" className="axis-text">
                    {label(m.month)}
                  </text>
                )}
                <rect
                  x={PAD.left + slot * i}
                  y={PAD.top}
                  width={slot}
                  height={plotH}
                  fill="transparent"
                  tabIndex={0}
                  aria-label={`${label(m.month, true)}: revenue ${money(m.revenue)}, costs ${money(m.costs)}, ${up ? "profit" : "loss"} ${money(Math.abs(m.profit))}${onSelect ? (selected === m.month ? ". Selected; press to clear" : ". Press to break this month down") : ""}`}
                  aria-pressed={onSelect ? selected === m.month : undefined}
                  role={onSelect ? "button" : undefined}
                  style={onSelect ? { cursor: "pointer" } : undefined}
                  onClick={() => onSelect?.(selected === m.month ? null : m.month)}
                  onKeyDown={(e) => {
                    if (onSelect && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      onSelect(selected === m.month ? null : m.month);
                    }
                  }}
                  onPointerEnter={() => setHover(i)}
                  onPointerLeave={() => setHover(null)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                />
              </g>
            );
          })}
        </svg>
      )}
      {h && (
        <Tooltip x={PAD.left + slot * hover! + slot / 2} y={PAD.top} width={width}>
          <strong>{money(h.profit)}</strong> {h.profit >= 0 ? "profit" : "loss"} in {label(h.month, true)}
          <div className="tip-rows">
            <span>Revenue {money(h.revenue)}</span>
            <span>Costs {money(h.costs)}</span>
            {h.margin !== null && <span>Margin {pct(h.margin)}</span>}
            <span>Cumulative {money(h.cumulative)}</span>
          </div>
        </Tooltip>
      )}
    </div>
  );
}

// --- Cash forecast (CF4) -------------------------------------------------------------------------------

export interface BalancePoint {
  key: string;
  label: string;
  balance: number;
  /** With the weighted pipeline; omitted when the pipeline is off. */
  withPipeline?: number;
  cashIn: number;
  cashOut: number;
}

export function BalanceLegend({ pipeline }: { pipeline: boolean }) {
  return (
    <div className="legend" aria-hidden="true">
      <span>
        <i style={{ background: BOOKED }} /> Booked
      </span>
      {pipeline && (
        <span>
          <i style={{ background: PIPELINE }} /> With the pipeline, weighted
        </span>
      )}
    </div>
  );
}

/** The running cash balance at the end of each week or month: booked, and with the weighted pipeline. */
export function BalanceChart({ points, selected = null, onSelect }: { points: BalancePoint[]; selected?: string | null; onSelect?: (key: string | null) => void }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const values = points.flatMap((p) => [p.balance, p.withPipeline ?? p.balance]);
  const lo = Math.min(0, ...values);
  const hi = Math.max(0, ...values, 1);
  const pad = (hi - lo) * 0.08;
  const [ylo, yhi] = [lo - (lo < 0 ? pad : 0), hi + pad];
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const x = (i: number) => PAD.left + (points.length > 1 ? (plotW * i) / (points.length - 1) : plotW / 2);
  const y = (v: number) => PAD.top + ((yhi - v) / (yhi - ylo)) * plotH;
  const every = labelEvery(points.length, plotW);
  const path = (f: (p: BalancePoint) => number) => points.map((p, i) => `${i ? "L" : "M"}${x(i)},${y(f(p))}`).join(" ");
  const showPipeline = points.some((p) => p.withPipeline !== undefined);
  const last = points.at(-1);
  const at = (e: React.PointerEvent<SVGRectElement> | React.MouseEvent<SVGRectElement>) => {
    const box = (e.currentTarget as SVGRectElement).getBoundingClientRect();
    return Math.max(0, Math.min(points.length - 1, Math.round(((e.clientX - box.left) / box.width) * (points.length - 1))));
  };
  const h = hover !== null ? points[hover] : null;
  const sel = points.findIndex((p) => p.key === selected);
  return (
    <div className="chart" ref={ref}>
      {width > 0 && points.length > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label="Running cash balance">
          {ticks(ylo, yhi).map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)} stroke={INK.grid} strokeWidth={1} />
              <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" className="axis-text">
                {compactMoney(t)}
              </text>
            </g>
          ))}
          <line x1={PAD.left} x2={PAD.left + plotW} y1={y(0)} y2={y(0)} stroke={lo < 0 ? "#941100" : INK.muted} strokeWidth={1} />
          {sel >= 0 && <line x1={x(sel)} x2={x(sel)} y1={PAD.top} y2={PAD.top + plotH} stroke={INK.primary} strokeWidth={2} opacity={0.35} />}
          {showPipeline && <path d={path((p) => p.withPipeline ?? p.balance)} fill="none" stroke={PIPELINE} strokeWidth={2} strokeDasharray="5 4" strokeLinejoin="round" />}
          <path d={path((p) => p.balance)} fill="none" stroke={BOOKED} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          {points.map((p, i) =>
            i % every === 0 ? (
              <text key={p.key} x={x(i)} y={HEIGHT - 6} textAnchor="middle" className="axis-text">
                {p.label}
              </text>
            ) : null,
          )}
          {last && (
            <>
              <circle cx={x(points.length - 1)} cy={y(last.balance)} r={4} fill={BOOKED} stroke="#FFFFFF" strokeWidth={2} />
              <text x={x(points.length - 1) + 8} y={y(last.balance) + 4} className="axis-text strong">
                {compactMoney(last.balance)}
              </text>
            </>
          )}
          {h && <line x1={x(hover!)} x2={x(hover!)} y1={PAD.top} y2={PAD.top + plotH} stroke={INK.secondary} strokeWidth={1} />}
          <rect
            x={PAD.left}
            y={PAD.top}
            width={plotW}
            height={plotH}
            fill="transparent"
            style={{ cursor: onSelect ? "pointer" : undefined }}
            onPointerMove={(e) => setHover(at(e))}
            onPointerLeave={() => setHover(null)}
            onClick={(e) => {
              const k = points[at(e)].key;
              onSelect?.(k === selected ? null : k);
            }}
          />
        </svg>
      )}
      {h && (
        <Tooltip x={x(hover!)} y={PAD.top} width={width}>
          <strong>{money(h.balance)}</strong> at the end of {h.label}
          <div className="tip-rows">
            <span>In {money(h.cashIn)}</span>
            <span>Out {money(h.cashOut)}</span>
            {h.withPipeline !== undefined && <span>With the pipeline {money(h.withPipeline)}</span>}
          </div>
        </Tooltip>
      )}
    </div>
  );
}

// --- Financial reports (FR2–FR5) ----------------------------------------------------------------------
// Categorical hues in fixed order (validated: CVD-separated, inside the lightness band); a neutral for
// Unmapped, Not yet assigned and Other. Money in is slot 1 and out slot 2: position (above or below
// the line) carries direction as well, so the pair never relies on color alone.

export const CATEGORICAL = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#6250d6", "#e34948"];
export const NEUTRAL = "#8C97A8"; // RAIN
const IN = CATEGORICAL[0];
const OUT = CATEGORICAL[1];

/** A hex color mixed toward black (t < 0) or white (t > 0) by |t|. */
function shade(hex: string, t: number): string {
  const to = t < 0 ? 0 : 255;
  const ch = (i: number) => Math.round(parseInt(hex.slice(i, i + 2), 16) * (1 - Math.abs(t)) + to * Math.abs(t)).toString(16).padStart(2, "0");
  return `#${ch(1)}${ch(3)}${ch(5)}`;
}

/** Past the eight hues, darker then lighter versions of each, so every key up to 24 gets a color of its own. */
const EXTENDED = [...CATEGORICAL, ...CATEGORICAL.map((c) => shade(c, -0.4)), ...CATEGORICAL.map((c) => shade(c, 0.45))];

/** A stable color per key: the order given (an entity keeps its hue as filters change), neutrals for leftovers. */
export function colorsFor(keys: readonly string[], neutral: (k: string) => boolean = () => false): Map<string, string> {
  const out = new Map<string, string>();
  let i = 0;
  for (const k of keys) out.set(k, neutral(k) ? NEUTRAL : EXTENDED[i++ % EXTENDED.length]);
  return out;
}

export interface FlowColumn {
  key: string;
  label: string;
  in: number;
  out: number;
  net: number;
  forecast: boolean;
}

export function CashFlowLegend({ forecast }: { forecast: boolean }) {
  return (
    <div className="legend" aria-hidden="true">
      <span>
        <i style={{ background: IN }} /> Money in
      </span>
      <span>
        <i style={{ background: OUT }} /> Money out
      </span>
      <span>
        <i style={{ background: INK.primary, height: 2 }} /> Net
      </span>
      {forecast && (
        <span>
          <i style={{ background: "transparent", border: `1px dashed ${INK.secondary}` }} /> Forecast
        </span>
      )}
    </div>
  );
}

/** Money in above the line and out below it per period, the net as a line; forecast periods paler, net dashed (FR2). */
export function CashFlowChart({ columns, selected = null, onSelect }: { columns: FlowColumn[]; selected?: string | null; onSelect?: (key: string | null) => void }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const H = 240;
  const top = Math.max(1, ...columns.map((c) => Math.max(c.in, c.net)));
  const bottom = Math.min(0, ...columns.map((c) => Math.min(-c.out, c.net)));
  const pad = (top - bottom) * 0.06;
  const [ylo, yhi] = [bottom - pad, top + pad];
  const plotW = Math.max(0, width - PAD.left - 16);
  const plotH = H - PAD.top - PAD.bottom;
  const band = columns.length ? plotW / columns.length : 0;
  const barW = Math.max(4, Math.min(36, band * 0.56));
  const cx = (i: number) => PAD.left + band * i + band / 2;
  const y = (v: number) => PAD.top + ((yhi - v) / (yhi - ylo)) * plotH;
  const every = labelEvery(columns.length, plotW);
  const h = hover !== null ? columns[hover] : null;
  const netPath = (pick: (c: FlowColumn, i: number) => boolean) =>
    columns
      .map((c, i) => ({ c, i }))
      .filter(({ c, i }) => pick(c, i))
      .map(({ c, i }, k) => `${k ? "L" : "M"}${cx(i)},${y(c.net)}`)
      .join(" ");
  const firstForecast = columns.findIndex((c) => c.forecast);
  /** A bar from the zero line down to `v` (below), square at the line, rounded at its end. */
  const downPath = (x0: number, x1: number, yEnd: number, yLine: number) => {
    const hgt = yEnd - yLine;
    if (hgt < 0.5) return "";
    const r = Math.min(4, hgt, (x1 - x0) / 2);
    return `M${x0},${yLine} V${yEnd - r} Q${x0},${yEnd} ${x0 + r},${yEnd} H${x1 - r} Q${x1},${yEnd} ${x1},${yEnd - r} V${yLine} Z`;
  };
  return (
    <div className="chart" ref={ref}>
      {width > 0 && columns.length > 0 && (
        <svg width={width} height={H} role="img" aria-label="Money in and out by period, with the net">
          {ticks(ylo, yhi).map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)} stroke={INK.grid} strokeWidth={1} />
              <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" className="axis-text">
                {compactMoney(t)}
              </text>
            </g>
          ))}
          <line x1={PAD.left} x2={PAD.left + plotW} y1={y(0)} y2={y(0)} stroke={INK.muted} strokeWidth={1} />
          {columns.map((c, i) => {
            const x0 = cx(i) - barW / 2;
            const dim = selected !== null && c.key !== selected;
            return (
              <g key={c.key} opacity={dim ? 0.35 : c.forecast ? 0.5 : 1}>
                {c.in > 0 && <path d={columnPath(x0, x0 + barW, y(c.in), y(0) - GAP / 2, true)} fill={IN} />}
                {c.out > 0 && <path d={downPath(x0, x0 + barW, y(-c.out), y(0) + GAP / 2)} fill={OUT} />}
              </g>
            );
          })}
          <path d={netPath((c) => !c.forecast)} fill="none" stroke={INK.primary} strokeWidth={2} strokeLinejoin="round" />
          {firstForecast >= 0 && <path d={netPath((c, i) => c.forecast || i === firstForecast - 1)} fill="none" stroke={INK.primary} strokeWidth={2} strokeDasharray="5 4" strokeLinejoin="round" />}
          {hover !== null && <circle cx={cx(hover)} cy={y(columns[hover].net)} r={4} fill={INK.primary} stroke="#FFFFFF" strokeWidth={2} />}
          {columns.map((c, i) =>
            i % every === 0 ? (
              <text key={`l-${c.key}`} x={cx(i)} y={H - 6} textAnchor="middle" className="axis-text">
                {c.label}
              </text>
            ) : null,
          )}
          {columns.map((c, i) => (
            <rect
              key={`hit-${c.key}`}
              x={PAD.left + band * i}
              y={PAD.top}
              width={band}
              height={plotH}
              fill="transparent"
              style={{ cursor: onSelect ? "pointer" : undefined }}
              onPointerEnter={() => setHover(i)}
              onPointerLeave={() => setHover(null)}
              onClick={() => onSelect?.(c.key === selected ? null : c.key)}
            />
          ))}
        </svg>
      )}
      {h && (
        <Tooltip x={cx(hover!)} y={PAD.top} width={width}>
          <strong>{h.label}</strong>
          {h.forecast ? " (forecast)" : ""}
          <div className="tip-rows">
            <span>In {money(h.in)}</span>
            <span>Out {money(h.out)}</span>
            <span>Net {money(h.net)}</span>
          </div>
        </Tooltip>
      )}
    </div>
  );
}

export interface RingSlice {
  key: string;
  label: string;
  amount: number;
  color: string;
}

/** A ring of slices with the total in the middle (FR4, FR5). Hover reads a slice; click drills into it. */
export function RingChart({ slices, total, caption, onSelect }: { slices: RingSlice[]; total: number; caption: string; onSelect?: (key: string) => void }) {
  const [hover, setHover] = useState<string | null>(null);
  const size = 240;
  const r = 104;
  const thick = 30;
  const positive = slices.filter((s) => s.amount > 0);
  const sum = positive.reduce((t, s) => t + s.amount, 0) || 1;
  let start = -Math.PI / 2;
  const arcs = positive.map((s) => {
    const end = start + (s.amount / sum) * Math.PI * 2;
    const arc = { s, a0: start, a1: end };
    start = end;
    return arc;
  });
  const pt = (a: number, rad: number) => `${size / 2 + rad * Math.cos(a)},${size / 2 + rad * Math.sin(a)}`;
  const path = (x0: number, x1: number) => {
    const ro = r;
    const ri = r - thick;
    if (x1 - x0 >= Math.PI * 2 - 1e-6) return `M${pt(0, ro)} A${ro},${ro} 0 1 1 ${pt(Math.PI, ro)} A${ro},${ro} 0 1 1 ${pt(0, ro)} M${pt(0, ri)} A${ri},${ri} 0 1 0 ${pt(Math.PI, ri)} A${ri},${ri} 0 1 0 ${pt(0, ri)} Z`;
    const large = x1 - x0 > Math.PI ? 1 : 0;
    return `M${pt(x0, ro)} A${ro},${ro} 0 ${large} 1 ${pt(x1, ro)} L${pt(x1, ri)} A${ri},${ri} 0 ${large} 0 ${pt(x0, ri)} Z`;
  };
  const h = positive.find((s) => s.key === hover);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={caption} className="ring">
      {arcs.map(({ s, a0, a1 }) => (
        <path
          key={s.key}
          d={path(a0, a1)}
          fill={s.color}
          stroke="#FFFFFF"
          strokeWidth={2}
          opacity={hover && hover !== s.key ? 0.45 : 1}
          style={{ cursor: onSelect ? "pointer" : undefined }}
          onPointerEnter={() => setHover(s.key)}
          onPointerLeave={() => setHover(null)}
          onClick={() => onSelect?.(s.key)}
        >
          <title>{`${s.label}: ${money(s.amount)} (${pct(s.amount / sum)})`}</title>
        </path>
      ))}
      <text x={size / 2} y={size / 2 - 4} textAnchor="middle" className="ring-total">
        {h ? money(h.amount) : money(total)}
      </text>
      <text x={size / 2} y={size / 2 + 16} textAnchor="middle" className="axis-text">
        {h ? `${h.label}, ${pct(h.amount / sum)}` : caption}
      </text>
    </svg>
  );
}

export interface FlowNode {
  id: string;
  label: string;
  color: string;
}

/**
 * The flow from clients into income and out to spending (FR3): three columns, each node as tall as its
 * money, each link as wide. Labels sit beside their nodes with the amount and its share of income.
 */
export function FlowChart({ links, nodes }: { links: { from: string; to: string; amount: number }[]; nodes: FlowNode[] }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<string | null>(null);
  const node = new Map(nodes.map((n) => [n.id, n]));
  const left = [...new Set(links.filter((l) => l.to === "Income").map((l) => l.from))];
  const right = [...new Set(links.filter((l) => l.from === "Income").map((l) => l.to))];
  const amountOf = (id: string, side: "from" | "to") => links.filter((l) => l[side] === id).reduce((t, l) => t + l.amount, 0);
  const totalIn = left.reduce((t, id) => t + amountOf(id, "from"), 0);
  const totalOut = right.reduce((t, id) => t + amountOf(id, "to"), 0);
  const total = Math.max(totalIn, totalOut, 1);
  const gap = 6;
  // Each node gets at least a label's height, so small categories don't stack their labels; ribbons
  // keep their true width, so the money still reads in proportion.
  const MIN_SLOT = 30;
  const base = Math.max(280, Math.max(left.length, right.length) * 36);
  const usable = (n: number) => base - 30 - gap * Math.max(0, n - 1);
  const scale = Math.min(usable(left.length), usable(right.length)) / total;
  const labelW = Math.min(220, Math.max(130, width * 0.24));
  const nodeW = 14;
  const xL = labelW;
  const xM = width / 2 - nodeW / 2;
  const xR = width - labelW - nodeW;
  const stack = (ids: string[], side: "from" | "to") => {
    let y0 = 10;
    const boxes = new Map(
      ids.map((id) => {
        const hgt = Math.max(2, amountOf(id, side) * scale);
        const box = { y: y0, h: hgt, slot: Math.max(hgt, MIN_SLOT) };
        y0 += box.slot + gap;
        return [id, box] as const;
      }),
    );
    return { boxes, end: y0 };
  };
  const { boxes: L, end: endL } = stack(left, "from");
  const { boxes: R, end: endR } = stack(right, "to");
  const H = Math.max(base, endL + 24, endR + 24);
  const midH = total * scale;
  const income = totalIn - amountOf("From reserves", "from");
  const share = (v: number) => (income > 0 ? pct(v / income) : "");
  const ribbon = (x0: number, y0: number, x1: number, y1: number, hgt: number) => {
    const c = (x0 + x1) / 2;
    return `M${x0},${y0} C${c},${y0} ${c},${y1} ${x1},${y1} L${x1},${y1 + hgt} C${c},${y1 + hgt} ${c},${y0 + hgt} ${x0},${y0 + hgt} Z`;
  };
  let midIn = 10;
  let midOut = 10;
  const leftRibbons = left.map((id) => {
    const box = L.get(id)!;
    const d = ribbon(xL + nodeW, box.y, xM, midIn, box.h);
    midIn += box.h;
    return { id, d };
  });
  const rightRibbons = right.map((id) => {
    const box = R.get(id)!;
    const d = ribbon(xM + nodeW, midOut, xR, box.y, box.h);
    midOut += box.h;
    return { id, d };
  });
  const fade = (id: string) => (hover && hover !== id ? 0.12 : 0.32);
  return (
    <div className="chart" ref={ref}>
      {width > 400 && links.length > 0 && (
        <svg width={width} height={H} role="img" aria-label="Income by client flowing to spending by category">
          {[...leftRibbons, ...rightRibbons].map((r) => (
            <path key={`${r.id}-${r.d.length}`} d={r.d} fill={node.get(r.id)?.color ?? NEUTRAL} opacity={fade(r.id)} onPointerEnter={() => setHover(r.id)} onPointerLeave={() => setHover(null)} />
          ))}
          <rect x={xM} y={10} width={nodeW} height={midH} rx={2} fill={INK.primary} />
          {left.map((id) => {
            const box = L.get(id)!;
            const v = amountOf(id, "from");
            return (
              <g key={`ln-${id}`} onPointerEnter={() => setHover(id)} onPointerLeave={() => setHover(null)}>
                <rect x={xL} y={box.y} width={nodeW} height={box.h} rx={2} fill={node.get(id)?.color ?? NEUTRAL} />
                <text x={xL - 8} y={box.y + box.slot / 2 - 2} textAnchor="end" className="axis-text strong">
                  {node.get(id)?.label ?? id}
                </text>
                <text x={xL - 8} y={box.y + box.slot / 2 + 12} textAnchor="end" className="axis-text">
                  {money(v)} {id === "From reserves" ? "" : share(v)}
                </text>
              </g>
            );
          })}
          {right.map((id) => {
            const box = R.get(id)!;
            const v = amountOf(id, "to");
            return (
              <g key={`rn-${id}`} onPointerEnter={() => setHover(id)} onPointerLeave={() => setHover(null)}>
                <rect x={xR} y={box.y} width={nodeW} height={box.h} rx={2} fill={node.get(id)?.color ?? NEUTRAL} />
                <text x={xR + nodeW + 8} y={box.y + box.slot / 2 - 2} className="axis-text strong">
                  {node.get(id)?.label ?? id}
                </text>
                <text x={xR + nodeW + 8} y={box.y + box.slot / 2 + 12} className="axis-text">
                  {money(v)} {share(v)}
                </text>
              </g>
            );
          })}
          <text x={xM + nodeW / 2} y={H - 6} textAnchor="middle" className="axis-text strong">
            Income {money(income)}
          </text>
        </svg>
      )}
    </div>
  );
}
